/**
 * AddressInput - Google Places autocomplete address input.
 *
 * Provides a single text input that shows Google Places suggestions as the
 * user types. When a suggestion is selected, it parses the full address into
 * structured fields (street, line2, city, state, zip) and calls onSelect.
 *
 * Used everywhere in the app where an address is entered:
 *	 - Company addresses (primary + billing)
 *	 - Location/building addresses
 *	 - Work order project + billing addresses
 *	 - Contact addresses
 *	 - User profile addresses
 *
 * Requires REACT_APP_GOOGLE_PLACES_API_KEY to be set in .env.
 * If the key is not set, falls back to a standard text input (no autocomplete).
 *
 * @example
 * <AddressInput
 *	   value={address}
 *	   onChange={setAddress}
 *	   onSelect={(parsed) => {
 *		   setCity(parsed.city);
 *		   setState(parsed.state);
 *		   setZip(parsed.zip);
 *	   }}
 *	   placeholder="Enter address"
 * />
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Alert } from '../../atoms';

/** Parsed address components returned when a suggestion is selected */
export interface ParsedAddress {
	/** Full formatted address string (e.g., "123 Main St, Indianapolis, IN 46204") */
	formattedAddress: string;
	/** Street number + street name (e.g., "123 Main St") */
	street: string;
	/** Apartment, suite, unit — always empty from Google, user fills in separately */
	line2: string;
	/** City name */
	city: string;
	/** State abbreviation (e.g., "IN", "OH") */
	state: string;
	/** ZIP/postal code */
	zip: string;
	/** Country (defaults to "US") */
	country: string;
	/** Latitude from geocoding */
	lat: number | null;
	/** Longitude from geocoding */
	lng: number | null;
}

export interface AddressInputProps {
	/** Current input value */
	value: string;
	/** Called on every keystroke (controlled input) */
	onChange: (value: string) => void;
	/** Called when a Google Places suggestion is selected with parsed address components */
	onSelect?: (parsed: ParsedAddress) => void;
	/** Input placeholder text */
	placeholder?: string;
	/** HTML id attribute */
	id?: string;
	/** Additional CSS classes */
	className?: string;
	/** Disabled state */
	disabled?: boolean;
	/** Error styling */
	hasError?: boolean;
}

/**
 * Parse a Google Places place result into structured address components.
 * Extracts street_number, route, locality, administrative_area_level_1,
 * postal_code, and country from the address_components array.
 */
const parsePlaceResult = (place: google.maps.places.PlaceResult): ParsedAddress => {
	const components = place.address_components || [];
	const get = (type: string, useShort = false): string => {
		const comp = components.find((c) => c.types.includes(type));
		return comp ? (useShort ? comp.short_name : comp.long_name) : '';
	};

	const streetNumber = get('street_number');
	const route = get('route');
	const street = streetNumber ? `${streetNumber} ${route}` : route;

	return {
		formattedAddress: place.formatted_address || '',
		street,
		line2: '',
		city: get('locality') || get('sublocality') || get('administrative_area_level_2'),
		state: get('administrative_area_level_1', true),
		zip: get('postal_code'),
		country: get('country', true) || 'US',
		lat: place.geometry?.location?.lat() ?? null,
		lng: place.geometry?.location?.lng() ?? null,
	};
};

/**
 * Custom event fired when Google rejects our API key. Google's own hook,
 * window.gm_authFailure, is a single global — so we install it once and
 * re-broadcast as an event that every mounted AddressInput can listen for.
 */
const AUTH_FAILURE_EVENT = 'addressinput:places-auth-failure';

/**
 * Install the global window.gm_authFailure hook (idempotent).
 *
 * Google calls this when the Maps script loads fine but the key is refused —
 * almost always RefererNotAllowedMapError (the origin the app is served from
 * is missing from the key's HTTP referrer allowlist) or an unenabled/unbilled
 * Places API. None of this reaches the getPlacePredictions callback, so
 * without this hook a rejected key looks exactly like a plain text field.
 */
const installAuthFailureHook = (): void => {
	const w = window as any;
	if (w.__addressInputAuthHookInstalled) return;
	w.__addressInputAuthHookInstalled = true;
	w.gm_authFailure = () => {
		console.error(
			'[AddressInput] Google rejected the Maps/Places API key. Check, in Google Cloud Console \u2192 Credentials, that this origin (' + window.location.origin + ') is in the key\'s HTTP referrer allowlist, that "Places API (New)" and "Maps JavaScript API" are enabled, and that billing is active on the project.'
		);
		window.dispatchEvent(new Event(AUTH_FAILURE_EVENT));
	};
};

/** Turn a non-OK PlacesServiceStatus into a short message for the user */
const describeStatus = (status: string): string => {
	switch (status) {
		case 'REQUEST_DENIED':
			return 'Address autocomplete is unavailable (API key rejected). Enter the address manually.';
		case 'OVER_QUERY_LIMIT':
			return 'Address autocomplete is temporarily unavailable (quota reached). Enter the address manually.';
		default:
			return 'Address autocomplete is unavailable. Enter the address manually.';
	}
};

/** Check if the Google Maps script is loaded */
const isGoogleLoaded = (): boolean => {
	return typeof google !== 'undefined' && typeof google.maps !== 'undefined' && typeof google.maps.places !== 'undefined';
};

const AddressInput: React.FC<AddressInputProps> = ({ value, onChange, onSelect, placeholder = 'Start typing an address...', id, className = '', disabled = false, hasError = false }) => {
	const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [googleReady, setGoogleReady] = useState(isGoogleLoaded());
	/* Non-null when Places is unavailable (key blocked, script blocked, quota
	   exhausted, no key configured). The input keeps working as plain text —
	   we just say so, rather than showing an empty dropdown forever. */
	const [placesError, setPlacesError] = useState<string | null>(null);
	const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
	const placesService = useRef<google.maps.places.PlacesService | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const dummyDiv = useRef<HTMLDivElement>(null);

	// Load Google Maps script if not already loaded
	useEffect(() => {
		installAuthFailureHook();

		const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
		if (!apiKey || isGoogleLoaded()) {
			setGoogleReady(isGoogleLoaded());
			// No key at all is a local setup gap, not something an end user can
			// act on — flag it in development only.
			if (!apiKey && process.env.NODE_ENV === 'development') {
				setPlacesError('Address autocomplete is off \u2014 REACT_APP_GOOGLE_PLACES_API_KEY is not set.');
			}
			return;
		}

		// Check if script tag already exists (another AddressInput may have loaded it)
		if (document.querySelector('script[src*="maps.googleapis.com"]')) {
			const checkReady = setInterval(() => {
				if (isGoogleLoaded()) {
					setGoogleReady(true);
					clearInterval(checkReady);
				}
			}, 100);
			return () => clearInterval(checkReady);
		}

		// Load the Google Maps script dynamically
		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&region=${process.env.REACT_APP_GOOGLE_PLACES_REGION || 'us'}`;
		script.async = true;
		script.defer = true;
		script.onload = () => setGoogleReady(true);
		// Network failure, ad/tracker blocker, or offline — the callback path
		// below never runs at all in this case, so report it here.
		script.onerror = () => {
			console.error('[AddressInput] Failed to load the Google Maps script.');
			setPlacesError('Address autocomplete could not load. Enter the address manually.');
		};
		document.head.appendChild(script);
	}, []);

	// Initialize Google services once ready
	useEffect(() => {
		if (!googleReady) return;
		autocompleteService.current = new google.maps.places.AutocompleteService();
		if (dummyDiv.current) {
			placesService.current = new google.maps.places.PlacesService(dummyDiv.current);
		}
	}, [googleReady]);

	// Surface a rejected API key (see installAuthFailureHook)
	useEffect(() => {
		const onAuthFailure = () => setPlacesError('Address autocomplete is unavailable (API key rejected). Enter the address manually.');
		window.addEventListener(AUTH_FAILURE_EVENT, onAuthFailure);
		return () => window.removeEventListener(AUTH_FAILURE_EVENT, onAuthFailure);
	}, []);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Fetch suggestions as user types
	const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		onChange(val);

		if (!val.trim() || !autocompleteService.current) {
			setSuggestions([]);
			setIsOpen(false);
			return;
		}

		// Request predictions from Google Places Autocomplete
		autocompleteService.current.getPlacePredictions(
			{
				input: val,
				componentRestrictions: { country: process.env.REACT_APP_GOOGLE_PLACES_REGION || 'us' },
				types: ['address'],
			},
			(predictions, status) => {
				if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
					setSuggestions(predictions);
					setIsOpen(true);
					setPlacesError(null);
					return;
				}

				setSuggestions([]);
				setIsOpen(false);

				// ZERO_RESULTS just means nothing matched what was typed so far —
				// expected on partial input. Every other status is a real fault
				// (REQUEST_DENIED, OVER_QUERY_LIMIT, ...) that the user would
				// otherwise experience as a field that simply never suggests.
				if (status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
					console.warn(`[AddressInput] Places Autocomplete returned ${status}.`);
					setPlacesError(describeStatus(status));
				}
			}
		);
	}, [onChange]);

	// Handle selecting a suggestion
	const handleSelect = useCallback((prediction: google.maps.places.AutocompletePrediction) => {
		// Set the input value to the selected description
		onChange(prediction.structured_formatting.main_text);
		setIsOpen(false);
		setSuggestions([]);

		// Fetch full place details to parse address components
		if (!placesService.current) return;
		placesService.current.getDetails(
			{ placeId: prediction.place_id, fields: ['address_components', 'formatted_address', 'geometry'] },
			(place, status) => {
				if (status === google.maps.places.PlacesServiceStatus.OK && place) {
					const parsed = parsePlaceResult(place);
					// Update the input to show the street address
					onChange(parsed.street);
					onSelect?.(parsed);
					return;
				}

				// Details lookup failed — the typed text stays put, but city /
				// state / ZIP won't auto-fill, so say why.
				console.warn(`[AddressInput] Places getDetails returned ${status}.`);
				setPlacesError(describeStatus(status));
			}
		);
	}, [onChange, onSelect]);

	// Border classes based on error state
	const borderClass = hasError
		? 'border-red-500 focus:ring-red-500'
		: 'border-brand-border focus:border-brand-primary focus:ring-brand-primary';

	return (
		<div ref={wrapperRef} className="address-input relative" data-ref="molecule-address-input">
			{/* Hidden div required by PlacesService constructor */}
			<div ref={dummyDiv} className="hidden" />

			{/* Address text input */}
			<input
				id={id}
				type="text"
				value={value}
				onChange={handleInputChange}
				onFocus={() => suggestions.length > 0 && setIsOpen(true)}
				disabled={disabled}
				placeholder={googleReady ? placeholder : 'Enter address manually...'}
				className={`address-input__field w-full px-12px py-8px min-h-44px desktop:min-h-36px text-16px desktop:text-14px font-sans rounded-md border ${borderClass} outline-none focus:ring-2 focus:ring-opacity-20 disabled:opacity-50 disabled:cursor-not-allowed bg-white ${className}`}
				autoComplete="off"
			/>

			{/* Autocomplete unavailable — tell the user instead of leaving a field
				that looks identical to one that never had autocomplete. Typing
				still works; city/state/ZIP just have to be filled in by hand.
				The shared Alert atom rather than a hand-tinted <p>, so this
				reads like every other inline warning in the app. */}
			{placesError && <Alert variant="warning" size="sm" className="address-input__notice mt-4px">{placesError}</Alert>}

			{/* Google "powered by" indicator */}
			{googleReady && isOpen && suggestions.length > 0 && (
				<ul className="address-input__list absolute z-50 mt-4px w-full bg-white border border-brand-border rounded-md shadow-lg max-h-256px overflow-y-auto">
					{suggestions.map((s) => (
						<li key={s.place_id} onClick={() => handleSelect(s)} className="address-input__item px-12px py-8px text-14px font-sans cursor-pointer min-h-44px flex flex-col justify-center hover:bg-brand-surface transition-colors">
							{/* Main text (street address) in bold */}
							<span className="font-medium text-brand-text">{s.structured_formatting.main_text}</span>
							{/* Secondary text (city, state) in muted */}
							<span className="text-12px text-brand-text-muted">{s.structured_formatting.secondary_text}</span>
						</li>
					))}
					{/* Google branding attribution (required by Terms of Service) */}
					<li className="px-12px py-4px text-10px text-gray-400 text-right border-t border-brand-border-light">
						Powered by Google
					</li>
				</ul>
			)}
		</div>
	);
};

export default AddressInput;
